import { IconButton } from '@material-ui/core';
import type { Theme } from '@material-ui/core/styles';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';
import type { BigNumberish } from 'ethers';
import React from 'react';

import starIcon from '../../../common/images/staking/icon-star.svg';
import { Button, Colors } from '../../legacy/button/Button.component';
import { CountDown } from '../countdown/CountDown.component';
import styles from './BidModal.module.css';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    exitButton: {
      width: theme.spacing(5),
      height: theme.spacing(5),
    },
    exitIcon: {
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
      color: 'white',
    },
  }),
);

interface Props {
  auctionId: BigNumberish;
  title: string;
  seller: string;
  image: string;
  currentBid: string;
  walletBalance: string;
  endDate: Date;
  hideModal: () => void;
  handlePlaceBid?: (auctionId: BigNumberish, bid: string) => Promise<void>;
  getMoreStars?: () => void;
}

interface WalletProps {
  walletBalance: string;
  getMoreStars?: () => void;
}

interface BidModalProps {
  hideModal: () => void;
}

const WalletBalanceCard: React.FC<WalletProps> = ({
  walletBalance,
  getMoreStars,
}: WalletProps) => {
  return (
    <div className={styles.walletContainer}>
      <div className={styles.walletInfoContainer}>
        <div className={styles.starsImageContainer}>
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <img src={starIcon} />
        </div>
        <div className={styles.walletDiv}>
          <div className={styles.walletHeader}>WALLET BALANCE</div>
          <div
            className={styles.walletBody}
          >{`${walletBalance.toLocaleString()} MOGUL AVAILABLE`}</div>
        </div>
      </div>
      <Button color={Colors.COLOR_SCHEME_4} onClick={getMoreStars}>
        GET MORE
      </Button>
    </div>
  );
};

const Bid: React.FC<Props> = ({
  auctionId,
  hideModal,
  title,
  seller,
  image,
  currentBid,
  walletBalance,
  endDate,
  handlePlaceBid,
  getMoreStars,
}: Props) => {
  const classes = useStyles();
  const [bid, setBid] = React.useState('0');

  const updateBid = React.useCallback((value: string) => {
    const newBid = parseFloat(value);
    if (newBid < Number.MAX_SAFE_INTEGER) {
      setBid(value);
    }
  }, []);

  const placeBid = async () => {
    if (handlePlaceBid) {
      await handlePlaceBid(auctionId, bid);
      hideModal();
    }
  };

  return (
    <div className={styles.nftCardContainer}>
      <div className={styles.imageContainer}>
        <video className={styles.image} src={image} autoPlay loop muted />
        <div className={styles.countDownContainer}>
          <div className={styles.countDownLabel}>auction ends in</div>
          <CountDown date={endDate} numberSize="24px" />
        </div>
      </div>
      <div className={styles.nftDetails}>
        <div className={styles.headerContainer}>
          <div className={styles.titleContainer}>
            <div className={styles.title}>{title}</div>
            <div className={styles.profileName}>by {seller}</div>
          </div>
          <div className={styles.exitButtonContainer}>
            <IconButton className={classes.exitButton} onClick={hideModal}>
              <ClearIcon className={classes.exitIcon} />
            </IconButton>
          </div>
        </div>
        <form className={styles.newBidInputContainer}>
          <div className={styles.newBidInputLabel}>enter bid</div>
          <div className={styles.newBidInputUnit}>
            <input
              type="number"
              min="1"
              className={styles.newBidInput}
              placeholder="ENTER AMOUNT"
              value={bid}
              onChange={e => updateBid(e.target.value)}
            />
            MOGUL
          </div>
        </form>
        <div>
          <div className={styles.bidTitle}>must be greater than</div>
          <div className={styles.bidContainer}>
            <div className={styles.bidAmount}>
              {currentBid.toLocaleString()}
            </div>
            <div className={styles.bidUnit}> stars</div>
          </div>
        </div>
        <WalletBalanceCard
          walletBalance={walletBalance}
          getMoreStars={getMoreStars}
        />
        <div className={styles.placeBidButtonContainer}>
          {(currentBid > walletBalance ||
            parseFloat(bid) > parseFloat(walletBalance)) && (
            <div className={styles.notEnoughStars}>not enough stars</div>
          )}
          <Button
            color={Colors.COLOR_SCHEME_2}
            className={styles.placeBidButton}
            onClick={placeBid}
            disabled={
              bid === '' ||
              parseFloat(bid) <= parseFloat(currentBid) ||
              currentBid > walletBalance ||
              parseFloat(bid) > parseFloat(walletBalance)
            }
          >
            Place Bid
          </Button>
        </div>
      </div>
    </div>
  );
};

export const BidModal =
  (
    auctionId: BigNumberish,
    title: string,
    seller: string,
    image: string,
    currentBid: string,
    walletBalance: string,
    endDate: Date,
    handlePlaceBid?: (auctionId: BigNumberish, bid: string) => Promise<void>,
    getMoreStars?: () => void,
  ) =>
  ({ hideModal }: BidModalProps) => {
    return (
      <Bid
        auctionId={auctionId}
        title={title}
        seller={seller}
        image={image}
        currentBid={currentBid}
        walletBalance={walletBalance}
        endDate={endDate}
        hideModal={hideModal}
        handlePlaceBid={handlePlaceBid}
        getMoreStars={getMoreStars}
      />
    );
  };
